"use client";

import { useState } from "react";
import Link from "next/link";
import { BlogPost } from "@/lib/blog-supabase";

interface Props {
  initialPosts: BlogPost[];
  categories: string[];
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
}

export default function BlogIndexClient({ initialPosts, categories }: Props) {
  const [activeCategory, setActiveCategory] = useState("All");

  const filtered =
    activeCategory === "All"
      ? initialPosts
      : initialPosts?.filter((p) => p?.category === activeCategory);

  return (
    <>
      <section className="bg-gradient-to-b from-navy to-navy-dark text-white pt-32 pb-20 px-5">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block text-gold text-xs font-bold uppercase tracking-widest mb-4 bg-gold/10 border border-gold/20 rounded-full px-4 py-1.5">
            PCB Vacation Rental Insights
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
            The Premier Property{" "}
            <span className="text-gold italic font-light">Blog</span>
          </h1>
          <p className="text-white/65 text-lg sm:text-xl leading-relaxed max-w-2xl mx-auto">
            Expert guides for Panama City Beach Airbnb hosts — pricing strategies, tax compliance, cleaning standards, and everything in between.
          </p>
        </div>
      </section>
      <section className="bg-white border-b border-sand-dark sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-4">
          <div className="flex gap-2 overflow-x-auto">
            {categories?.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeCategory === cat
                    ? "bg-navy text-white" :"bg-sand text-navy/60 hover:text-navy hover:bg-sand-dark"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered?.map((post) => (
            <article
              key={post?.slug}
              className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-luxury transition-all duration-300 group flex flex-col"
            >
              <Link href={`/blog/${post?.slug}`} className="block overflow-hidden aspect-video">
                <img
                  src={post?.featuredImage}
                  alt={post?.featuredImageAlt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </Link>

              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-semibold text-gold uppercase tracking-wider bg-gold/10 px-2.5 py-1 rounded-full">
                    {post?.category}
                  </span>
                  <span className="text-xs text-navy/40 flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {post?.readTime} min read
                  </span>
                </div>

                <Link href={`/blog/${post?.slug}`}>
                  <h2 className="text-lg font-bold text-navy mb-3 group-hover:text-gold transition-colors duration-200 line-clamp-2">
                    {post?.title}
                  </h2>
                </Link>

                <p className="text-navy/60 text-sm leading-relaxed mb-4 line-clamp-3 flex-1">
                  {post?.excerpt}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {post?.tags?.slice(0, 3)?.map((tag) => (
                    <span key={tag} className="text-xs text-navy/50 bg-sand px-2 py-0.5 rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-sand">
                  <div className="flex items-center gap-2">
                    <img
                      src={post?.authorAvatar}
                      alt={`${post?.authorName} profile photo`}
                      className="w-7 h-7 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-xs font-medium text-navy">{post?.authorName}</p>
                      <p className="text-xs text-navy/40">
                        {formatDate(post.publishDate)}
                      </p>
                    </div>
                  </div>
                  <Link
                    href={`/blog/${post?.slug}`}
                    className="text-xs font-semibold text-gold hover:text-gold/80 flex items-center gap-1 transition-colors"
                  >
                    Read
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {filtered?.length === 0 && (
          <div className="text-center py-20 text-navy/40">
            <p className="text-lg">No posts in this category yet.</p>
          </div>
        )}
      </section>
    </>
  );
}
